// Files on a register item, grouped by document. Each document shows its current version; older versions stay
// downloadable. "New version" uploads a replacement (like Smartsheet's "upload new version").
import { useRef, useState } from 'react';
import { downloadFile, write } from '../lib/api';
import { uploadFile } from '../lib/files';
import { fmtBytes, fmtDateTime } from '../lib/format';
import { Badge, Empty, errText, useToast } from './kit';
import { canView, useViewer } from './Viewer';
import type { Org } from './item/shared';

export type FileRow = {
  id: string;
  original_name: string;
  mime: string | null;
  size_bytes: number | null;
  kind: 'file' | 'link';
  external_url: string | null;
  doc_key: string;
  version: number;
  is_current: boolean;
  comment_id: string | null;
  uploaded_at: string | null;
  uploaded_by_name: string | null;
  source: string;
  from_org_id?: string | null;
  in_pack?: boolean;
  notes?: string | null;
  created_at: string;
  deleted_at: string | null;
};

const ext = (f: FileRow) => (f.kind === 'link' ? 'LINK' : (f.original_name.split('.').pop() ?? '').slice(0, 4).toUpperCase() || 'FILE');

export function ItemFiles({
  files,
  siteId,
  itemId,
  canEdit,
  onChanged,
  orgs = [],
}: {
  files: FileRow[];
  siteId: string;
  itemId: string;
  canEdit: boolean;
  onChanged: () => void;
  /** organisations for the "From" note on each file */
  orgs?: Org[];
}) {
  const toast = useToast();
  const addIn = useRef<HTMLInputElement>(null);
  const verIn = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<FileRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [over, setOver] = useState(false);
  const [editing, setEditing] = useState<{ id: string; from: string; note: string; focus: 'from' | 'note' } | null>(null);
  const orgName = (id: string | null | undefined) => (id ? orgs.find((o) => o.id === id)?.name ?? '—' : null);

  async function saveFoot() {
    if (!editing) return;
    try {
      await write('files', 'update', { id: editing.id, values: { from_org_id: editing.from || null, notes: editing.note.trim() || null }, summary: 'Updated file From / Note' });
      setEditing(null);
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  const live = files.filter((f) => !f.deleted_at);
  const docs = new Map<string, FileRow[]>();
  for (const f of live) docs.set(f.doc_key, [...(docs.get(f.doc_key) ?? []), f]);
  const groups = [...docs.values()]
    .map((vs) => {
      const sorted = [...vs].sort((a, b) => b.version - a.version);
      const current = sorted.find((v) => v.is_current) ?? sorted[0];
      return { current, older: sorted.filter((v) => v.id !== current.id) };
    })
    .sort((a, b) => String(b.current.uploaded_at ?? b.current.created_at).localeCompare(String(a.current.uploaded_at ?? a.current.created_at)));

  const viewer = useViewer();
  const dl = (f: FileRow) => void downloadFile(f.id).catch((e) => toast({ tone: 'error', text: errText(e) }));
  const asView = (f: FileRow) => ({ id: f.id, name: f.original_name, mime: f.mime, kind: f.kind });

  async function add(list: FileList | File[]) {
    for (const f of Array.from(list)) {
      setBusy(`Uploading ${f.name}…`);
      try {
        await uploadFile(f, { siteId, linkedTable: 'items', linkedId: itemId });
      } catch (e) {
        toast({ tone: 'error', text: `${f.name}: ${errText(e)}` });
      }
    }
    setBusy(null);
    onChanged();
  }

  async function newVersion(file: File, of: FileRow) {
    setBusy(`Uploading new version of ${of.original_name}…`);
    try {
      await uploadFile(file, { siteId, linkedTable: 'items', linkedId: itemId, replaces: of.id, keepOriginal: true });
      toast({ tone: 'ok', text: `New version of ${of.original_name} uploaded` });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
    setBusy(null);
    setReplacing(null);
    onChanged();
  }

  async function removeDoc(g: { current: FileRow; older: FileRow[] }) {
    const n = 1 + g.older.length;
    if (!window.confirm(`Delete ${g.current.original_name}${n > 1 ? ` and its ${n - 1} older version(s)` : ''}? It can be restored from the audit log.`)) return;
    const batch = crypto.randomUUID();
    try {
      for (const f of [g.current, ...g.older]) await write('files', 'delete', { id: f.id, batch_id: batch, summary: `Deleted ${f.original_name} v${f.version}` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  return (
    <div>
      {canEdit ? (
        <div
          className={`drop ${over ? 'over' : ''}`}
          onDragOver={(e) => (e.preventDefault(), setOver(true))}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            if (e.dataTransfer.files.length) void add(e.dataTransfer.files);
          }}
        >
          {busy ?? (
            <>
              Drop files here or{' '}
              <button type="button" className="linkbtn" onClick={() => addIn.current?.click()}>
                choose files
              </button>
            </>
          )}
          <input ref={addIn} type="file" multiple hidden onChange={(e) => e.target.files && void add(e.target.files)} />
          <input
            ref={verIn}
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f && replacing) void newVersion(f, replacing);
              e.target.value = '';
            }}
          />
        </div>
      ) : null}
      {groups.length === 0 ? (
        <Empty>No files yet.</Empty>
      ) : (
        groups.map((g) => (
          <div className="doc" key={g.current.doc_key}>
            <span className="doc-ico" aria-hidden="true">
              {ext(g.current)}
            </span>
            <div className="doc-main">
              <div className="doc-name">
                <button
                  type="button"
                  className="linkbtn"
                  title={canView(asView(g.current)) ? 'View' : g.current.kind === 'link' ? 'Open link' : 'Download'}
                  onClick={() => viewer(asView(g.current), groups.map((x) => asView(x.current)))}
                >
                  {g.current.original_name}
                </button>{' '}
                {g.current.version > 1 || g.older.length ? <Badge tone="dark">v{g.current.version}</Badge> : null}{' '}
                {g.current.comment_id ? <Badge>on a comment</Badge> : null}
              </div>
              <div className="small muted">
                {g.current.kind === 'link' ? 'Link' : fmtBytes(g.current.size_bytes)} · {g.current.uploaded_by_name ? `${g.current.uploaded_by_name} · ` : ''}
                {fmtDateTime(g.current.uploaded_at ?? g.current.created_at)}
                {g.current.source === 'smartsheet' ? ' · from Smartsheet' : ''}
              </div>
              {editing?.id === g.current.id ? (
                <div className="ffoot-edit">
                  <label>
                    From
                    <select value={editing.from} autoFocus={editing.focus === 'from'} onChange={(e) => setEditing({ ...editing, from: e.target.value })}>
                      <option value="">—</option>
                      {orgs.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grow">
                    Note
                    <input
                      value={editing.note}
                      autoFocus={editing.focus === 'note'}
                      placeholder="e.g. Official RFI as issued to the architects"
                      onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                      onKeyDown={(e) => (e.key === 'Enter' ? void saveFoot() : e.key === 'Escape' ? setEditing(null) : null)}
                    />
                  </label>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => void saveFoot()}>
                    Save
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </div>
              ) : orgs.length || g.current.notes ? (
                <div className="ffoot">
                  {(['from', 'note'] as const).map((k, i) => {
                    const val = k === 'from' ? orgName(g.current.from_org_id) : g.current.notes;
                    const body = (
                      <>
                        <span className="muted">{k === 'from' ? 'From:' : 'Note:'}</span> {val ? <span className={k === 'from' ? 'strong' : ''}>{val}</span> : <em className="muted">{canEdit ? (k === 'from' ? 'who sent it?' : 'add a note') : '—'}</em>}
                      </>
                    );
                    return (
                      <span key={k} className="ffoot-part">
                        {i ? <span className="muted"> · </span> : null}
                        {canEdit ? (
                          <button type="button" className="qe" onClick={() => setEditing({ id: g.current.id, from: g.current.from_org_id ?? '', note: g.current.notes ?? '', focus: k })} aria-label={k === 'from' ? 'Edit who the file came from' : 'Edit the note'}>
                            {body}
                          </button>
                        ) : (
                          body
                        )}
                      </span>
                    );
                  })}
                </div>
              ) : null}
              <div className="cmt-actions">
                {g.current.kind === 'file' ? (
                  <button type="button" className="linkbtn" onClick={() => dl(g.current)}>
                    Download
                  </button>
                ) : null}
                {g.older.length ? (
                  <button type="button" className="linkbtn" onClick={() => setOpen((o) => ({ ...o, [g.current.doc_key]: !o[g.current.doc_key] }))}>
                    {open[g.current.doc_key] ? 'Hide' : 'Show'} {g.older.length} older version{g.older.length > 1 ? 's' : ''}
                  </button>
                ) : null}
                {canEdit && g.current.kind === 'file' ? (
                  <button
                    type="button"
                    className="linkbtn"
                    onClick={() => {
                      setReplacing(g.current);
                      verIn.current?.click();
                    }}
                  >
                    Upload new version
                  </button>
                ) : null}
                {canEdit ? (
                  <button type="button" className="linkbtn danger" onClick={() => void removeDoc(g)}>
                    Delete
                  </button>
                ) : null}
              </div>
              {open[g.current.doc_key] ? (
                <ul className="versions">
                  {g.older.map((v) => (
                    <li key={v.id}>
                      <Badge>v{v.version}</Badge>
                      <button type="button" className="linkbtn" onClick={() => viewer(asView(v), [asView(g.current), ...g.older.map(asView)])}>
                        {v.original_name}
                      </button>
                      <button type="button" className="linkbtn" onClick={() => dl(v)}>
                        Download
                      </button>
                      <span className="muted">
                        {fmtBytes(v.size_bytes)} · {v.uploaded_by_name ? `${v.uploaded_by_name} · ` : ''}
                        {fmtDateTime(v.uploaded_at ?? v.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
